import type { NpcInstanceV1 } from "@cold-case/shared/case-manifest";
import Phaser from "phaser";

import { FollowNudgePhaserChrome } from "../lib/follow-nudge-ui.js";
import { hasDialogMarkup, stripDialogMarkup } from "../lib/dialog-rich-text.js";
import { substituteDialog, type CoherenceJournal } from "../lib/dialog-parser.js";
import { isFollowingLeader } from "./player-follow.js";
import { npcInstanceDisplayName } from "../lib/manifest-display.js";
import { setPlayerOverlayOpen } from "../lib/player-input-gate.js";
import {
  NpcDialogSession,
  type NpcDialogCloseResult,
} from "../lib/npc-dialog-session.js";

import {
  bodyViewportHeight,
  computeDialogShell,
  DIALOG_FONT,
  PANEL_DESKTOP_MAX_H,
  type DialogShellMetrics,
} from "./dialog-layout.js";
import {
  caseNpcLineStyle,
  casePlayerLineStyle,
  createNpcHeader,
  positionNpcHeader,
  queueSouthPortraitIfMissing,
  southPortraitTextureKey,
  type NpcHeaderObjects,
} from "./npc-dialog-chrome.js";
import { MaskedScrollViewport } from "./npc-dialog-scroll.js";
import { UI_FONT, UI_SURFACE, UI_TEXT } from "./ui-theme.js";

const Z = 5000;
const FOOTER_H = 56;
const CHOICE_GAP = 8;
const TYPEAHEAD_MS = 18;
const LEADER_NPC_MIRROR_THROTTLE_MS = 100;

type OpenParams = {
  npc: NpcInstanceV1;
  getJournal: () => CoherenceJournal;
  playerCount: number;
  names: string[];
  victimName: string;
  onClose: (result: NpcDialogCloseResult) => void;
  readOnly?: boolean;
};

type BtnStyle = "primary" | "secondary" | "ghost";

export class NpcDialogPanel {
  private readOnly = false;
  private session: NpcDialogSession | null = null;
  private onCloseCb: ((r: NpcDialogCloseResult) => void) | null = null;
  private npcName = "";
  private dim: Phaser.GameObjects.Rectangle | null = null;
  private panel: Phaser.GameObjects.Rectangle | null = null;
  private header: NpcHeaderObjects | null = null;
  private headerBottom = 0;
  private npcSkinId = "";
  private transcriptBg: Phaser.GameObjects.Rectangle | null = null;
  private scroll: MaskedScrollViewport | null = null;
  private typeaheadTimer: Phaser.Time.TimerEvent | null = null;
  private typeaheadFull = "";
  private typeaheadShown = "";
  private typeaheadRow: Phaser.GameObjects.Text | null = null;
  private typeaheadPrefix = "";
  /** Transcript lines fully revealed (0..length); active typeahead is at this index. */
  private typeaheadDisplayedThrough = 0;
  private transcriptContainer: Phaser.GameObjects.Container | null = null;
  private transcriptMask: Phaser.GameObjects.Graphics | null = null;
  private choiceRows: Phaser.GameObjects.Text[] = [];
  private closeBtn: Phaser.GameObjects.Text | null = null;
  private shell: DialogShellMetrics | null = null;
  private transcriptTop = 0;
  private transcriptH = 0;
  private transcriptLeft = 0;
  private choiceTop = 0;
  private closeY = 0;
  private maxChoiceBottom = 0;
  private mirrorPending: Omit<
    import("@cold-case/shared/player-follow").LeaderMirrorNpc,
    "kind"
  > | null = null;
  private mirrorThrottleTimer: Phaser.Time.TimerEvent | null = null;
  private nudgeChrome: FollowNudgePhaserChrome | null = null;

  private footerReservePx(shell: DialogShellMetrics): number {
    const closeBtnReserve = shell.mobile ? 52 : 44;
    return FOOTER_H + closeBtnReserve + shell.pad;
  }

  private syncFooterLayout(shell: DialogShellMetrics): void {
    const panelBottom = shell.panelCy + shell.panelH * 0.5;
    shell.panelBottom = panelBottom;
    this.closeY = panelBottom - shell.pad;
    this.maxChoiceBottom = panelBottom - FOOTER_H - shell.pad;
  }
  private names: string[] = [];
  private victimName = "";

  constructor(private readonly scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return this.session !== null;
  }

  flashNudgedAck(): void {
    this.nudgeChrome?.showNudgedAck();
  }

  open(params: OpenParams): void {
    if (this.session) {
      this.close();
    } else {
      this.destroyUi();
    }
    this.onCloseCb = params.onClose;
    this.readOnly = !!params.readOnly;
    this.npcName = npcInstanceDisplayName(params.npc);
    this.npcSkinId = params.npc.npc_id;
    this.names = params.names;
    this.victimName = params.victimName;
    queueSouthPortraitIfMissing(this.scene, this.npcSkinId, () => this.relayout());
    this.typeaheadDisplayedThrough = 0;
    this.session = new NpcDialogSession(
      params.npc,
      params.getJournal,
      params.playerCount,
      params.names,
      params.victimName
    );
    if (!this.readOnly) {
      this.session.start();
    }
    setPlayerOverlayOpen("npc-dialog", true);
    this.buildChrome(this.npcName);
    this.refresh();
  }

  /** Follower mirror — same UI, no input; session state comes from leader snapshot. */
  openMirror(params: OpenParams & { mirror: import("@cold-case/shared/player-follow").LeaderMirrorNpc }): void {
    this.open({ ...params, readOnly: true });
    this.typeaheadDisplayedThrough = 0;
    this.session?.applyMirrorState({
      phase: params.mirror.phase,
      transcript: params.mirror.transcript,
      questionPage: params.mirror.questionPage,
    });
    this.refresh();
  }

  syncMirror(mirror: import("@cold-case/shared/player-follow").LeaderMirrorNpc): void {
    if (!this.session || !this.readOnly) return;
    const prevLen = this.session.transcript.length;
    this.session.applyMirrorState({
      phase: mirror.phase,
      transcript: mirror.transcript,
      questionPage: mirror.questionPage,
    });
    if (mirror.transcript.length > prevLen) {
      this.typeaheadDisplayedThrough = Math.min(
        this.typeaheadDisplayedThrough,
        prevLen
      );
    }
    this.refresh();
  }

  relayout(): void {
    if (!this.session) return;
    const session = this.session;
    const savedThrough = this.typeaheadDisplayedThrough;
    const savedScrollY = this.scroll?.scrollY ?? 0;
    const savedStick = this.scroll?.stickToBottom ?? true;
    this.destroyUi();
    this.session = session;
    this.typeaheadDisplayedThrough = savedThrough;
    this.buildChrome(this.npcName);
    this.refresh();
    if (this.scroll) {
      this.scroll.stickToBottom = savedStick;
      if (!savedStick) this.scroll.scrollY = savedScrollY;
      this.scroll.reflow();
    }
  }

  forceCloseWithoutProgress(): void {
    if (!this.session) return;
    this.session.dismissWithoutProgress();
    this.close();
  }

  close(): void {
    if (!this.session) return;
    const instanceId = this.session.getInstanceId();
    const result = this.session.close();
    const cb = this.onCloseCb;
    this.destroyUi();
    this.session = null;
    this.onCloseCb = null;
    setPlayerOverlayOpen("npc-dialog", false);
    if (!this.readOnly) {
      this.emitLeaderNpcMirror(
        {
          caseNotesOpen: false,
          dialogOpen: false,
          instanceId,
          phase: "done",
          transcript: [],
          questionPage: 0,
        },
        { immediate: true }
      );
    }
    cb?.(result);
  }

  private emitLeaderNpcMirror(
    payload: Omit<import("@cold-case/shared/player-follow").LeaderMirrorNpc, "kind">,
    opts?: { immediate?: boolean }
  ): void {
    if (this.readOnly) return;
    this.mirrorPending = payload;
    if (opts?.immediate) {
      this.flushLeaderNpcMirrorNow();
      return;
    }
    if (this.mirrorThrottleTimer) return;
    this.mirrorThrottleTimer = this.scene.time.delayedCall(
      LEADER_NPC_MIRROR_THROTTLE_MS,
      () => {
        this.mirrorThrottleTimer = null;
        this.flushLeaderNpcMirrorNow();
      }
    );
  }

  private flushLeaderNpcMirrorNow(): void {
    if (this.mirrorThrottleTimer) {
      this.mirrorThrottleTimer.remove(false);
      this.mirrorThrottleTimer = null;
    }
    if (!this.mirrorPending) return;
    const payload = this.mirrorPending;
    this.mirrorPending = null;
    this.scene.game.events.emit("ccc_leader_mirror_npc", payload);
  }

  dismissWithoutProgress(): void {
    this.session?.dismissWithoutProgress();
    this.close();
  }

  private destroyUi(): void {
    if (this.mirrorThrottleTimer) {
      this.mirrorThrottleTimer.remove(false);
      this.mirrorThrottleTimer = null;
    }
    this.mirrorPending = null;
    this.nudgeChrome?.destroy();
    this.nudgeChrome = null;
    this.clearTypeahead();
    this.typeaheadDisplayedThrough = 0;
    this.scroll?.unbind();
    this.scroll = null;
    this.dim?.destroy();
    this.panel?.destroy();
    this.header?.title.destroy();
    this.header?.portrait?.destroy();
    this.header = null;
    this.transcriptBg?.destroy();
    this.transcriptMask?.destroy();
    this.transcriptContainer?.destroy();
    for (const t of this.choiceRows) t.destroy();
    this.choiceRows = [];
    this.closeBtn?.destroy();
    this.dim = null;
    this.panel = null;
    this.transcriptBg = null;
    this.transcriptMask = null;
    this.transcriptContainer = null;
    this.closeBtn = null;
    this.shell = null;
  }

  private buildChrome(npcName: string): void {
    const vw = this.scene.scale.width;
    const vh = this.scene.scale.height;
    const shell = computeDialogShell(vw, vh, FOOTER_H + 120);
    this.shell = shell;

    const portraitKey = southPortraitTextureKey(this.scene, this.npcSkinId);
    this.header = createNpcHeader(this.scene, shell, Z + 2, npcName, portraitKey);
    this.headerBottom = this.header.metrics.headerBottom - shell.panelTop;
    this.transcriptTop = shell.panelTop + this.headerBottom;
    this.transcriptH = bodyViewportHeight(
      shell.panelH,
      this.headerBottom,
      this.footerReservePx(shell)
    );
    this.transcriptLeft = shell.panelCx - shell.innerW * 0.5;
    this.choiceTop = this.transcriptTop + this.transcriptH + 12;
    this.syncFooterLayout(shell);

    this.dim = this.scene.add
      .rectangle(vw * 0.5, vh * 0.5, vw, vh, shell.dimColor, shell.dimAlpha)
      .setScrollFactor(0)
      .setDepth(Z)
      .setInteractive();

    this.panel = this.scene.add
      .rectangle(shell.panelCx, shell.panelCy, shell.panelW, shell.panelH, UI_SURFACE.panel, 0.98)
      .setStrokeStyle(2, UI_SURFACE.panelStroke, 1)
      .setScrollFactor(0)
      .setDepth(Z + 1)
      .setInteractive()
      .on("pointerdown", (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
      });

    this.transcriptBg = this.scene.add
      .rectangle(
        this.transcriptLeft + shell.innerW * 0.5,
        this.transcriptTop + this.transcriptH * 0.5,
        shell.innerW,
        this.transcriptH,
        UI_SURFACE.transcript,
        1
      )
      .setStrokeStyle(1, UI_SURFACE.transcriptStroke, 1)
      .setScrollFactor(0)
      .setDepth(Z + 2);

    this.transcriptContainer = this.scene.add
      .container(this.transcriptLeft, this.transcriptTop)
      .setScrollFactor(0)
      .setDepth(Z + 3);

    this.transcriptMask = this.scene.add.graphics().setScrollFactor(0).setDepth(Z + 3);
    this.transcriptMask.fillStyle(0xffffff, 1);
    this.transcriptMask.fillRect(
      this.transcriptLeft,
      this.transcriptTop,
      shell.innerW,
      this.transcriptH
    );
    this.transcriptMask.setVisible(false);
    this.transcriptContainer.setMask(this.transcriptMask.createGeometryMask());

    this.scroll = new MaskedScrollViewport({
      scene: this.scene,
      hitTarget: this.transcriptBg,
      getContainer: () => this.transcriptContainer,
      getViewportTop: () => this.transcriptTop,
      getViewportH: () => this.transcriptH,
      getLineGap: () => shell.lineGap,
    });
    this.scroll.stickToBottom = true;
    this.scroll.bind();
  }

  private updateTranscriptGeometry(): void {
    const shell = this.shell;
    if (!shell || !this.transcriptBg || !this.transcriptMask) return;
    this.transcriptBg
      .setPosition(
        this.transcriptLeft + shell.innerW * 0.5,
        this.transcriptTop + this.transcriptH * 0.5
      )
      .setSize(shell.innerW, this.transcriptH);
    this.transcriptMask.clear();
    this.transcriptMask.fillStyle(0xffffff, 1);
    this.transcriptMask.fillRect(
      this.transcriptLeft,
      this.transcriptTop,
      shell.innerW,
      this.transcriptH
    );
  }

  private clearTypeahead(): void {
    this.typeaheadTimer?.remove(false);
    this.typeaheadTimer = null;
    this.typeaheadRow = null;
    this.typeaheadFull = "";
    this.typeaheadShown = "";
    this.typeaheadPrefix = "";
  }

  private startTypeaheadOnLine(
    fullText: string,
    prefix: string,
    onComplete: () => void
  ): void {
    this.clearTypeahead();
    if (!this.transcriptContainer) return;
    if (fullText.length === 0) {
      onComplete();
      return;
    }
    const children = this.transcriptContainer.list as Phaser.GameObjects.Text[];
    const last = children[children.length - 1];
    if (!last) return;
    this.typeaheadRow = last;
    this.typeaheadFull = fullText;
    this.typeaheadShown = "";
    this.typeaheadPrefix = prefix;
    last.setText(prefix);
    this.typeaheadTimer = this.scene.time.addEvent({
      delay: TYPEAHEAD_MS,
      repeat: fullText.length,
      callback: () => {
        const idx = this.typeaheadShown.length;
        if (idx >= this.typeaheadFull.length) {
          this.clearTypeahead();
          this.scroll?.reflow({ forceBottom: this.scroll.stickToBottom });
          onComplete();
          return;
        }
        this.typeaheadShown += this.typeaheadFull[idx]!;
        const row = this.typeaheadRow;
        if (row) {
          row.setText(this.typeaheadPrefix + this.typeaheadShown);
          const lineBottom = row.y + row.height;
          this.scroll!.stickToBottom = true;
          this.scroll?.reflow({ forceBottom: false });
          this.scroll?.scrollToKeepLineBottom(lineBottom);
        }
      },
    });
  }

  private renderTranscript(session: NpcDialogSession): void {
    if (!this.transcriptContainer || !this.shell) return;
    const shell = this.shell;
    const rows = session.transcript;

    this.clearTypeahead();
    this.transcriptContainer.removeAll(true);
    let y = 6;
    let typeaheadNpc: { prefix: string; body: string } | null = null;
    let typeaheadAnchor: Phaser.GameObjects.Text | null = null;
    const wrapW = shell.innerW - 12;

    const appendLine = (
      prefix: string,
      displayBody: string,
      isPlayer: boolean
    ): Phaser.GameObjects.Text => {
      const style = isPlayer
        ? casePlayerLineStyle(shell.bodyFont)
        : caseNpcLineStyle(shell.bodyFont);
      const t = this.scene.add.text(0, y, prefix + displayBody, {
        ...style,
        wordWrap: { width: wrapW },
      });
      t.setResolution(2);
      this.transcriptContainer!.add(t);
      y += t.height + shell.lineGap;
      return t;
    };

    const displayBody = (raw: string): string => {
      const t = raw.trim();
      if (!t.length) return "…";
      return stripDialogMarkup(t);
    };

    for (let i = 0; i < this.typeaheadDisplayedThrough && i < rows.length; i++) {
      const row = rows[i]!;
      const isPlayer = row.speaker === "player";
      const prefix = isPlayer ? "You · " : `${this.npcName} · `;
      appendLine(prefix, displayBody(row.text), isPlayer);
    }

    while (this.typeaheadDisplayedThrough < rows.length) {
      const row = rows[this.typeaheadDisplayedThrough]!;
      const isPlayer = row.speaker === "player";
      const prefix = isPlayer ? "You · " : `${this.npcName} · `;
      const body = displayBody(row.text);
      appendLine(prefix, "", isPlayer);
      typeaheadNpc = { prefix, body };
      if (
        this.typeaheadDisplayedThrough > 0 &&
        rows[this.typeaheadDisplayedThrough - 1]?.speaker === "player"
      ) {
        typeaheadAnchor = this.transcriptContainer!.list[
          this.transcriptContainer!.list.length - 2
        ] as Phaser.GameObjects.Text;
      }
      break;
    }

    this.transcriptContainer.setX(this.transcriptLeft);
    this.scroll!.stickToBottom = true;
    this.scroll?.reflow({ forceBottom: true });
    if (typeaheadAnchor) {
      this.scroll?.scrollToKeepLineBottom(typeaheadAnchor.y + typeaheadAnchor.height);
    }

    if (typeaheadNpc) {
      this.startTypeaheadOnLine(typeaheadNpc.body, typeaheadNpc.prefix, () => {
        this.typeaheadDisplayedThrough++;
        this.renderTranscript(session);
      });
    }
  }

  private addChoiceButton(
    label: string,
    choiceY: number,
    onPick: () => void,
    variant: BtnStyle = "primary"
  ): Phaser.GameObjects.Text | null {
    const shell = this.shell;
    if (!shell) return null;
    const choiceW = shell.panelW - shell.pad * 2;
    const bg =
      variant === "primary"
        ? UI_SURFACE.btnPrimary
        : variant === "secondary"
          ? UI_SURFACE.btnSecondary
          : UI_SURFACE.btnGhost;
    const displayLabel = hasDialogMarkup(label) ? stripDialogMarkup(label) : label;
    const row = this.scene.add
      .text(shell.panelCx, choiceY, displayLabel, {
        fontFamily: UI_FONT,
        fontSize: `${DIALOG_FONT.choice}px`,
        color: this.readOnly ? UI_TEXT.muted : UI_TEXT.primary,
        fontStyle: hasDialogMarkup(label) ? "600" : "500",
        backgroundColor: this.readOnly ? UI_SURFACE.btnGhost : bg,
        padding: { x: 16, y: 12 },
        wordWrap: { width: choiceW - 24 },
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(Z + 4);
    if (!this.readOnly) {
      row.setInteractive({ useHandCursor: true });
      row.on("pointerdown", (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
        onPick();
      });
    }
    this.choiceRows.push(row);
    return row;
  }

  private measureChoiceHeight(label: string): number {
    const shell = this.shell;
    if (!shell) return 40;
    const plain = stripDialogMarkup(label);
    const choiceW = shell.panelW - shell.pad * 2;
    const probe = this.scene.add.text(0, 0, plain, {
      fontFamily: UI_FONT,
      fontSize: `${DIALOG_FONT.choice}px`,
      wordWrap: { width: choiceW - 24 },
      padding: { x: 16, y: 12 },
    });
    const h = probe.height;
    probe.destroy();
    return h;
  }

  private renderCloseButton(): void {
    if (this.readOnly) return;
    const shell = this.shell;
    if (!shell) return;
    const w = shell.panelW - shell.pad * 2;
    this.closeBtn = this.scene.add
      .text(shell.panelCx, this.closeY, "Close", {
        fontFamily: UI_FONT,
        fontSize: `${shell.buttonFont}px`,
        color: UI_TEXT.link,
        fontStyle: "600",
        backgroundColor: UI_SURFACE.btnClose,
        padding: { x: 20, y: 10 },
      })
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(Z + 5)
      .setInteractive({ useHandCursor: true });
    this.closeBtn.setWordWrapWidth(w - 24, true);
    this.closeBtn.on("pointerdown", (
      _pointer: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      event: Phaser.Types.Input.EventData
    ) => {
      event.stopPropagation();
      this.dismissWithoutProgress();
    });
  }

  private refresh(): void {
    const session = this.session;
    const shell = this.shell;
    if (!session || !shell) return;

    for (const t of this.choiceRows) t.destroy();
    this.choiceRows = [];
    this.closeBtn?.destroy();
    this.closeBtn = null;

    type ChoiceDef = { label: string; onPick: () => void; variant: BtnStyle };
    const defs: ChoiceDef[] = [];

    if (session.isGreeting()) {
      defs.push({
        label: "I have some questions.",
        onPick: () => {
          session.advanceFromGreeting();
          this.refresh();
        },
        variant: "primary",
      });
      defs.push({
        label: "Leave.",
        onPick: () => {
          session.endConversationEarly();
          this.refresh();
        },
        variant: "secondary",
      });
    } else if (session.canPickQuestions()) {
      for (const q of session.visibleQuestions()) {
        defs.push({
          label: substituteDialog(
            q.question_prompt.trim() || "Follow-up question",
            this.names,
            this.victimName
          ),
          onPick: () => {
            session.pickQuestion(q.question_id);
            this.refresh();
          },
          variant: "primary",
        });
      }
      if (session.hasMoreQuestionPages()) {
        defs.push({
          label: "Ask about something else…",
          onPick: () => {
            session.showMoreQuestionTopics();
            this.refresh();
          },
          variant: "ghost",
        });
      }
      defs.push({
        label: "I'm done talking.",
        onPick: () => {
          session.endConversationEarly();
          this.refresh();
        },
        variant: "secondary",
      });
    } else if (session.isConversationDone()) {
      defs.push({
        label: "Leave conversation.",
        onPick: () => this.close(),
        variant: "primary",
      });
    }

    const heights = defs.map((d) => this.measureChoiceHeight(d.label));
    let totalChoiceH = 0;
    for (let i = 0; i < heights.length; i++) {
      totalChoiceH += heights[i]! + (i > 0 ? CHOICE_GAP : 0);
    }

    const choiceBlockH = totalChoiceH + CHOICE_GAP + FOOTER_H + shell.pad;
    let panelBottom = shell.panelCy + shell.panelH * 0.5;
    const neededBottom = this.transcriptTop + 80 + choiceBlockH + shell.pad;
    if (!shell.mobile && neededBottom > panelBottom) {
      const newPanelH = Math.min(
        PANEL_DESKTOP_MAX_H,
        Math.max(shell.panelH, neededBottom - shell.panelTop + shell.pad)
      );
      if (newPanelH > shell.panelH) {
        shell.panelH = newPanelH;
        shell.panelTop = shell.panelCy - newPanelH * 0.5;
        if (this.header) {
          const portraitKey = southPortraitTextureKey(this.scene, this.npcSkinId);
          const metrics = positionNpcHeader(
            this.scene,
            shell,
            this.header,
            this.npcName,
            portraitKey
          );
          this.headerBottom = metrics.headerBottom - shell.panelTop;
        }
        this.transcriptTop = shell.panelTop + this.headerBottom;
        this.transcriptH = bodyViewportHeight(
          shell.panelH,
          this.headerBottom,
          this.footerReservePx(shell)
        );
        this.panel?.setSize(shell.panelW, shell.panelH);
        this.panel?.setPosition(shell.panelCx, shell.panelCy);
        panelBottom = shell.panelCy + shell.panelH * 0.5;
        shell.panelBottom = panelBottom;
      }
    }
    const maxTranscriptBottom = panelBottom - choiceBlockH;
    this.transcriptH = Math.max(80, Math.min(this.transcriptH, maxTranscriptBottom - this.transcriptTop));
    this.choiceTop = this.transcriptTop + this.transcriptH + 12;
    this.syncFooterLayout(shell);

    let choiceY = this.choiceTop;
    for (let i = 0; i < defs.length; i++) {
      const def = defs[i]!;
      const rowH = heights[i]!;
      if (choiceY + rowH > this.maxChoiceBottom) break;
      this.addChoiceButton(def.label, choiceY, def.onPick, def.variant);
      choiceY += rowH + CHOICE_GAP;
    }

    this.updateTranscriptGeometry();
    this.renderTranscript(session);
    this.renderCloseButton();
    this.syncNudgeChrome(shell);

    if (this.session && !this.readOnly) {
      this.emitLeaderNpcMirror({
        caseNotesOpen: false,
        dialogOpen: true,
        instanceId: this.session.getInstanceId(),
        phase: this.session.phase,
        transcript: [...this.session.transcript],
        questionPage: this.session.getQuestionPage(),
      });
    }
  }

  private syncNudgeChrome(shell: DialogShellMetrics): void {
    this.nudgeChrome?.destroy();
    this.nudgeChrome = new FollowNudgePhaserChrome(this.scene, Z + 6);
    if (this.readOnly && isFollowingLeader()) {
      this.nudgeChrome.mountFollowerNudge(shell);
    } else if (!this.readOnly) {
      this.nudgeChrome.mountLeaderAck(shell);
    } else {
      this.nudgeChrome.destroy();
      this.nudgeChrome = null;
    }
  }
}
